import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Card, CardContent, CardHeader, CircularProgress, Box,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useFetch } from '../../../hooks/use-fetch';
import { BASE_ADMIN_API_URL } from '../../../config';
import dayjs from 'dayjs';

function descendingComparator(a, b, orderBy) {
    const va = a[orderBy] ?? '';
    const vb = b[orderBy] ?? '';
    if (vb < va) return -1;
    if (vb > va) return 1;
    return 0;
}

function getComparator(order, orderBy) {
    return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

const columns = [
    { id: 'last_access', label: 'Last access', width: 160 },
    { id: 'name', label: 'Name', width: 180 },
    { id: 'mail', label: 'Email', width: 240 },
    { id: 'apps', label: 'Apps', width: 70 },
];

export default function UsersList() {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [order, setOrder] = useState('desc');
    const [orderBy, setOrderBy] = useState('last_access');
    const fetchWithAuth = useFetch();
    const navigate = useNavigate();

    useEffect(() => {
        let cancelled = false;

        async function load() {
            try {
                const response = await fetchWithAuth(`${BASE_ADMIN_API_URL}/users`);
                if (!response.ok) throw new Error(await response.text());
                const data = await response.json();
                if (!cancelled) setUsers(data);
            } catch (e) {
                console.error('Failed to load users', e);
            } finally {
                if (!cancelled) setLoading(false);
            }
        }

        load();
        return () => { cancelled = true; };
    }, [fetchWithAuth]);

    const handleSort = useCallback((columnId) => {
        setOrder(prev => orderBy === columnId && prev === 'asc' ? 'desc' : 'asc');
        setOrderBy(columnId);
    }, [orderBy]);

    const handleClick = useCallback((id) => {
        navigate(`${id}/`);
    }, [navigate]);

    const sorted = useMemo(() => {
        const rows = users.map(u => ({
            ...u,
            apps: u._count?.user_applications ?? 0,
        }));
        return rows.sort(getComparator(order, orderBy));
    }, [users, order, orderBy]);

    return (
        <Card>
            <CardHeader title="Users" />
            <CardContent>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    {columns.map(col => (
                                        <TableCell key={col.id} sx={{ width: col.width }}>
                                            <TableSortLabel
                                                active={orderBy === col.id}
                                                direction={orderBy === col.id ? order : 'asc'}
                                                onClick={() => handleSort(col.id)}
                                            >
                                                {col.label}
                                            </TableSortLabel>
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {sorted.map(user => (
                                    <TableRow
                                        key={user.id}
                                        hover
                                        sx={{ cursor: 'pointer' }}
                                        onClick={() => handleClick(user.id)}
                                    >
                                        <TableCell>{user.last_access ? dayjs(user.last_access).format('YYYY-MM-DD HH:mm') : '—'}</TableCell>
                                        <TableCell>{user.name || '—'}</TableCell>
                                        <TableCell>{user.mail || '—'}</TableCell>
                                        <TableCell>{user.apps}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </CardContent>
        </Card>
    );
}
